import React from "react"
import {Avatar, Box, Chip, Typography} from "@mui/material"
import {alpha} from "@mui/material/styles"
import PersonIcon from "@mui/icons-material/Person"
import SmartToyIcon from "@mui/icons-material/SmartToy"
import SupportAgentIcon from "@mui/icons-material/SupportAgent"
import {format} from "date-fns"

import type {UnidentifiedAttachment, UnidentifiedDirection, UnidentifiedThreadMessage} from "../types"

import {AttachmentChips} from "./AttachmentChips"
import {MessageBody} from "./MessageBody"

import {api} from "@indebted/api"
import {useNotification} from "@indebted/components/Notification"

function formatOccurredAt(occurredAt: string): string {
	return format(new Date(occurredAt), "d MMM yyyy, HH:mm")
}

function senderProps(direction: UnidentifiedDirection): {
	name: string
	avatar: React.ReactElement
	kindChip?: string
	outbound: boolean
} {
	const avatarSx = {width: 32, height: 32}
	const automatedSender = (kindChip: string) => ({
		name: "InDebted",
		avatar: (
			<Avatar sx={(theme) => ({...avatarSx, bgcolor: alpha(theme.palette.primary.main, 0.12)})}>
				<SmartToyIcon fontSize="small" color="primary" />
			</Avatar>
		),
		kindChip,
		outbound: true,
	})
	switch (direction) {
		case "inbound":
			return {
				name: "Sender",
				avatar: (
					<Avatar sx={{...avatarSx, bgcolor: "secondary.main"}}>
						<PersonIcon fontSize="small" />
					</Avatar>
				),
				outbound: false,
			}
		case "agent-reply":
			return {
				name: "InDebted",
				avatar: (
					<Avatar sx={{...avatarSx, bgcolor: "primary.main"}}>
						<SupportAgentIcon fontSize="small" />
					</Avatar>
				),
				outbound: true,
			}
		case "information-request":
			return automatedSender("Information request")
		case "acknowledgement":
			return automatedSender("Acknowledgement")
		case "case-verification-link":
			return automatedSender("Verification link")
		case "ai-auto-reply":
		default:
			return automatedSender("Auto-reply")
	}
}

function deliveryChipProps(status: string): {label: string; color: "success" | "warning" | "error"} | null {
	switch (status) {
		case "delivered":
			return {label: "Delivered", color: "success"}
		case "temporarily-undelivered":
			return {label: "Delivery delayed", color: "warning"}
		case "permanently-undelivered":
			return {label: "Not delivered", color: "error"}
		default:
			return null
	}
}

function attachmentURL(channel: string, conversationID: string, providerMessageID: string, index: number): string {
	return api.url(`${api.inbox.unidentifiedItemPath(channel, conversationID, providerMessageID)}/attachments/${index}`)
}

function MessageRow({
	message,
	threadSubject,
	senderAddress,
	channel,
	conversationID,
}: {
	message: UnidentifiedThreadMessage
	threadSubject: string
	senderAddress: string
	channel: string
	conversationID: string
}) {
	const {notification} = useNotification()
	const isInbound = message.Direction === "inbound"
	const sender = senderProps(message.Direction)
	const deliveryChip = message.DeliveryStatus ? deliveryChipProps(message.DeliveryStatus) : null
	const hasMetaRow =
		Boolean(message.AuthoredBy) ||
		Boolean(message.ClassificationCategory) ||
		Boolean(message.EscalationReason) ||
		Boolean(deliveryChip)

	const downloadAttachment = async (attachment: UnidentifiedAttachment) => {
		try {
			// API Gateway base64-decodes a response only when the request asks for a
			// binary media type, so a plain navigation would save the base64 text.
			const response = await fetch(
				attachmentURL(channel, conversationID, message.ProviderMessageID, attachment.Index),
				{headers: {Accept: "application/octet-stream"}},
			)
			if (!response.ok) {
				notification.error("Failed to download the attachment.")
				return
			}
			const objectURL = URL.createObjectURL(await response.blob())
			const link = document.createElement("a")
			link.href = objectURL
			link.download = attachment.Filename
			document.body.appendChild(link)
			link.click()
			link.remove()
			URL.revokeObjectURL(objectURL)
		} catch {
			notification.error("Failed to download the attachment.")
		}
	}

	return (
		<Box component="li" display="flex" gap={1.5} sx={{listStyle: "none"}}>
			{sender.avatar}
			<Box
				flex={1}
				minWidth={0}
				p={2.5}
				border="1px solid"
				borderColor={sender.outbound ? "transparent" : "grey.200"}
				borderRadius={2}
				sx={(theme) => ({
					bgcolor: sender.outbound ? alpha(theme.palette.primary.main, 0.04) : "background.paper",
				})}
			>
				<Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1} mb={0.25}>
					<Box display="flex" alignItems="center" gap={1} minWidth={0} flexWrap="wrap">
						<Typography variant="subtitle2" color="textPrimary">
							{sender.name}
						</Typography>
						{sender.kindChip && <Chip label={sender.kindChip} size="small" variant="outlined" />}
						{isInbound && senderAddress && (
							<Typography variant="caption" color="textSecondary">
								{senderAddress}
							</Typography>
						)}
					</Box>
					<Typography variant="caption" color="textSecondary" whiteSpace="nowrap" mt={0.25}>
						{formatOccurredAt(message.OccurredAt)}
					</Typography>
				</Box>
				{message.Subject && message.Subject !== threadSubject && (
					<Typography variant="caption" color="textSecondary" display="block">
						Subject: {message.Subject}
					</Typography>
				)}
				{hasMetaRow && (
					<Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
						{message.AuthoredBy && (
							<Typography variant="caption" color="textSecondary">
								by {message.AuthoredBy}
							</Typography>
						)}
						{isInbound && message.ClassificationCategory && (
							<Chip label={message.ClassificationCategory} size="small" variant="outlined" />
						)}
						{isInbound && message.EscalationReason && (
							<Chip label={message.EscalationReason} size="small" color="warning" variant="outlined" />
						)}
						{deliveryChip && (
							<>
								<Chip
									label={deliveryChip.label}
									size="small"
									color={deliveryChip.color}
									variant="outlined"
								/>
								{message.DeliveryStatus === "delivered" && message.DeliveryOccurredAt && (
									<Typography variant="caption" color="textSecondary">
										at {formatOccurredAt(message.DeliveryOccurredAt)}
									</Typography>
								)}
							</>
						)}
					</Box>
				)}
				<MessageBody body={message.Body} quoted={message.Quoted} />
				<AttachmentChips
					attachments={message.Attachments}
					// Inbound only: a reply is stored under the inbound's provider message ID, so
					// an outbound chip would download the inbound's file at that index, not its own.
					onDownload={isInbound ? downloadAttachment : undefined}
				/>
			</Box>
		</Box>
	)
}

export {MessageRow, formatOccurredAt}
